import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import Filter from "./components/Filter";
import gqlPayments from "../../gql/payments";
import Pagination from "../../components/Pagination";
import DetailsModal from "./components/DetailsModal";
import { Chip, Tooltip } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { useTranslation } from "react-i18next";
import AppButton from "../../shared/AppButton";
import AppTable from "../../shared/AppTable";
import TableSkeletonLoader from "../../common/TableLoading";
import "../PreAuth/styles/pre-auth.css";
import toast from "react-hot-toast";

function PaymentList(props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentDate = new Date();
  const storage_key = "payments_list_filters";

  const [item, setItem] = useState({});
  const [showFilter, setShowFilter] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Load saved filters from sessionStorage before initializing state
  const getInitialFilters = () => {
    const saved = sessionStorage.getItem(storage_key);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error("Error loading saved filters:", error);
      }
    }
    return null;
  };

  const savedFilters = getInitialFilters();

  const defaultFromDate = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1,
  ).padStart(2, "0")}-01`;
  const defaultToDate = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1,
  ).padStart(2, "0")}-${new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0,
  ).getDate()}`;

  const [fromDate, setFromDate] = useState(
    savedFilters?.from_date || defaultFromDate,
  );
  const [toDate, setToDate] = useState(savedFilters?.to_date || defaultToDate);

  const [limit, setLimit] = useState(10);
  const [pageNo, setPageNo] = useState(1);

  const [refetchFilterVariables, setRefetchFilterVariables] = useState(
    savedFilters || {
      limit: limit,
      from_date: fromDate,
      to_date: toDate,
    },
  );

  const { loading, error, data, refetch } = useQuery(
    gqlPayments.GET_ALL_PAYMENTS,
    {
      variables: {
        input: {
          from_date: fromDate,
          to_date: toDate,
          limit: 10,
          page: 1,
          ...(savedFilters?.payer_id && { payer_id: savedFilters.payer_id }),
          ...(savedFilters?.provider_name_en && {
            provider_name_en: savedFilters.provider_name_en,
          }),
          ...(savedFilters?.confirmation_status && {
            confirmation_status: savedFilters.confirmation_status,
          }),
        },
      },
      fetchPolicy: "network-only",
    },
  );

  const {
    loading: pollLoading,
    error: pollError,
    data: pollData,
    refetch: refetchPoll,
  } = useQuery(gqlPayments.PAYMENT_POLL_REQ, {
    skip: true,
  });

  const pollClicked = () => {
    toast.loading("Requesting!");
    refetchPoll().then(async (res) => {
      await refetch({
        input: {
          from_date: fromDate,
          to_date: toDate,
          limit: 10,
          page: 1,
        },
      });
    });
  };

  const onPageChange = (currentPage) => {
    setPageNo(currentPage);
    let vars = {
      ...refetchFilterVariables,
      limit: limit,
      page: currentPage,
      from_date: fromDate,
      to_date: toDate,
    };
    refetch({ input: vars });
  };

  const getStatusStyles = (status) => {
    const s = status?.toLowerCase();
    if (s === "complete" || s === "paid" || s === "confirmed") {
      return { bg: "#dcfce7", text: "#15803d" };
    } else if (s === "failed" || s === "rejected" || s === "cancelled") {
      return { bg: "#fee2e2", text: "#b91c1c" };
    } else if (s === "pending" || s === "partial") {
      return { bg: "#fef9c3", text: "#854d0e" };
    } else {
      return { bg: "#f1f5f9", text: "#475569" };
    }
  };
  const onDataRowClicked = (item) => {
    navigate(`/payments/details`, { state: { paymentData: item } });
  };
  const columns = [
    {
      header: t("SQ #"),
      render: (row, index) => pageNo * limit + index + 1 - limit,
    },
    {
      header: t("Confirmation Status"),
      render: (row) => {
        if (!row?.confirmation_status) return "---";
        const styles = getStatusStyles(row.confirmation_status);
        return (
          <Chip
            label={row.confirmation_status}
            size="small"
            sx={{
              backgroundColor: styles.bg,
              color: styles.text,
              fontWeight: "700",
              fontSize: "10px",
              borderRadius: "6px",
              textTransform: "uppercase",
              height: 20,
            }}
          />
        );
      },
    },
    {
      header: t("Payment Status"),
      render: (row) => {
        if (!row?.payment_status) return "---";
        const styles = getStatusStyles(row.payment_status);
        return (
          <Chip
            label={row.payment_status}
            size="small"
            sx={{
              backgroundColor: styles.bg,
              color: styles.text,
              fontWeight: "700",
              fontSize: "10px",
              borderRadius: "6px",
              textTransform: "uppercase",
              height: 20,
            }}
          />
        );
      },
    },
    {
      header: t("Payer"),
      render: (row) => (
          <span className="text-[11px] text-gray-700 w-[220px] block whitespace-pre-wrap">
            {row?.payer_name_en}
          </span>
      ),
    },
    {
      header: t("Provider"),
      render: (row) => (
          <span className="text-[11px] text-gray-700 w-[220px] block whitespace-pre-wrap">
            {row?.providers_name_en}
          </span>
      ),
    },
    {
      header: t("Payment Amount"),
      render: (row) => (
        <span className="font-bold">
          {row.payment_amount}{" "}
          <small className="text-gray-400 font-normal">
            {row.payment_currency}
          </small>
        </span>
      ),
    },
    { header: t("Payment Note Date"), field: "payment_note_date" },
  ];

  return (
    <>
      <div className="pre_auth_container !p-0">
        {/* Action bar */}
        <div className="flex justify-between gap-1.5 flex-col sm:flex-row mb-2">
          <span className="font-semibold text-black/70 text-lg ">
            {t("Payments")}
            <span className=" text-lg ml-1">
              ({data?.listPayments?.total || 0}{" "}
              <span className="text-xs font-bold italic">{t("Entries")}</span>)
            </span>
          </span>
        </div>

        {/* Filter panel */}
        {showFilter && (
          <div className="bg-white border border-slate-200 rounded-lg p-2 mb-2">
            <Filter
              refetch={refetch}
              setPageNo={setPageNo}
              setLimit={setLimit}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
              setFromDate={setFromDate}
              setToDate={setToDate}
              setRefetchFilterVariables={setRefetchFilterVariables}
              storageKey={storage_key}
            />
          </div>
        )}

        {/* Table */}
        <AppTable
          columns={columns}
          data={data?.listPayments?.data}
          loading={loading || pollLoading}
          onView={(row) => onDataRowClicked(row)}
          // onView={(row) => {
          //   setItem(row);
          //   setShowDetailsModal(true);
          // }}
          LoaderComponent={<TableSkeletonLoader columnCount={8} rowCount={8} />}
        />

        {/* Pagination */}
        <div>
          <Pagination
            currentPage={pageNo}
            totalPages={data?.listPayments?.pages}
            onPageChange={onPageChange}
          />
        </div>

        <DetailsModal
          showDetails={showDetailsModal}
          setShowDetails={setShowDetailsModal}
          item={item}
        />
      </div>
    </>
  );
}

export default PaymentList;
